import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { usePresencasDiarias } from '../../api/hooks/usePresencasDiarias';
import { useEventosAcesso } from '../../api/hooks/useEventosAcesso';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import { useAuthStore } from '../../store/authStore';
import { useSetupStatus } from '../../hooks/useSetupStatus';
import { useEscolaDashboardRealtime } from '../../hooks/useEscolaDashboardRealtime';
import { api } from '../../api/client';
import { useConsolidacao } from '../../api/hooks/useRelatorios';
import type { Aluno, Turma, Turno, Professor } from '../../types/models';
import { ROUTES } from '../../api/routes';

function shortTurmaLabel(nome: string, max = 10) {
  return nome.length > max ? `${nome.slice(0, max - 1)}…` : nome;
}

function monthBounds(year: number, month: number) {
  const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const last = new Date(year, month + 1, 0).getDate();
  const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { start, end };
}

type EscopoMetricas = {
  totalPrevistos?: number;
  totalPresentes?: number;
  totalAusentes?: number;
  totalInconsistencias?: number;
  totalEntraram?: number;
  aderenciaChamadas?: {
    totalTurmas?: number;
    comChamada?: number;
    taxa?: number;
  };
};

export type TurmaAulaAusente = { id: string; nome: string };

export type TurmaAulaSlot = {
  numeroAula: number;
  horaInicio?: string | null;
  horaFim?: string | null;
  materiaId?: string | null;
  materiaNome?: string | null;
  professorId?: string | null;
  professorNome?: string | null;
  horarioAulaId?: string | null;
  chamadaId?: string | null;
  status: string;
  totalPresentes?: number;
  totalAusentes?: number;
  totalAtrasados?: number;
  totalJustificados?: number;
  totalAlunos?: number;
  ausentes?: TurmaAulaAusente[];
};

export type GradeChamadasTurma = {
  turmaId: string;
  turmaNome?: string;
  chamadasEsperadas: number;
  chamadasFeitas: number;
  resumoChamada?: {
    numeroAula: number;
    totalPresentes: number;
    totalAusentes: number;
    totalAtrasados?: number;
    totalJustificados?: number;
    totalAlunos: number;
  } | null;
  ausentesUnicosDia?: TurmaAulaAusente[];
  aulas: TurmaAulaSlot[];
};

type ChamadaDoDiaItem = {
  id: string;
  turmaId: string;
  numeroAula?: number | null;
  status?: string;
  professor?: { id?: string; nome?: string };
  materia?: { id?: string; nome?: string };
  totalPresentes?: number;
  totalAusentes?: number;
  totalAlunos?: number;
  _count?: { alunos?: number };
};

type DashboardConsolidado = {
  turnoOperacional?: string | null;
  cards?: EscopoMetricas & { alertasAtivos?: number };
  escopos?: {
    dia?: EscopoMetricas;
    turno?: EscopoMetricas & { nome?: string };
  };
  operacao?: {
    gradeChamadasPorTurma?: GradeChamadasTurma[];
    chamadasDoDia?: ChamadaDoDiaItem[];
  };
  analitico?: {
    aderenciaChamadas?: {
      totalTurmas?: number;
      comChamada?: number;
      taxa?: number;
    };
    gradeChamadasPorTurma?: GradeChamadasTurma[];
    chamadasDoDia?: ChamadaDoDiaItem[];
  };
  censo?: { alunos?: number; turmas?: number };
};

/** Fallback: agrupa chamadasDoDia quando gradeChamadasPorTurma ainda não veio. */
function gradeFromChamadasDoDia(chamadas: ChamadaDoDiaItem[]): GradeChamadasTurma[] {
  const byTurma = new Map<string, TurmaAulaSlot[]>();
  for (const c of chamadas) {
    if (!c.turmaId) continue;
    const list = byTurma.get(c.turmaId) || [];
    list.push({
      numeroAula: c.numeroAula ?? list.length + 1,
      materiaNome: c.materia?.nome ?? null,
      materiaId: c.materia?.id ?? null,
      professorNome: c.professor?.nome ?? null,
      professorId: c.professor?.id ?? null,
      chamadaId: c.id,
      status: c.status || 'ABERTA',
      totalPresentes: c.totalPresentes ?? 0,
      totalAusentes: c.totalAusentes ?? 0,
      totalAlunos: c.totalAlunos ?? c._count?.alunos ?? 0,
    });
    byTurma.set(c.turmaId, list);
  }
  return [...byTurma.entries()].map(([turmaId, aulas]) => {
    const sorted = [...aulas].sort((a, b) => a.numeroAula - b.numeroAula);
    return {
      turmaId,
      chamadasEsperadas: sorted.length,
      chamadasFeitas: sorted.filter((a) => a.chamadaId && a.status !== 'PENDENTE').length,
      aulas: sorted,
    };
  });
}

export function useAdminDashboardPage() {
  const escolaId = useAuthStore((s) => s.escolaId);
  const user = useAuthStore((s) => s.user);
  const nomeEscola = useAuthStore((s) => s.nomeEscola);

  const { data: alunos = [] } = useCrud<Aluno>('alunos');
  const { data: turmas = [] } = useCrud<Turma>('turmas');
  const { data: turnos = [] } = useCrud<Turno>('turnos');
  const { data: professores = [] } = useCrud<Professor>('professores');

  const { hojeStr, isSimulation } = useHojeReferencia();
  const { connected: realtimeConnected } = useEscolaDashboardRealtime(escolaId);

  /** Poll curto só quando o WS está off (prod = sim). */
  const fallbackPollMs: number | false = realtimeConnected ? false : 25_000;

  const hoje = useMemo(() => {
    const [y, m, d] = hojeStr.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }, [hojeStr]);
  const dataFormatada = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(hoje);
  const dataCurta = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(hoje);
  const diaReferenciaLabel = isSimulation ? 'Dia simulado' : 'Dia atual';

  const [viewYear, setViewYear] = useState(() => hoje.getFullYear());
  const [viewMonth, setViewMonth] = useState(() => hoje.getMonth());
  const [selectedIso, setSelectedIso] = useState(hojeStr);

  useEffect(() => {
    setSelectedIso(hojeStr);
    setViewYear(hoje.getFullYear());
    setViewMonth(hoje.getMonth());
  }, [hojeStr, hoje]);

  const { start: mesInicio, end: mesFim } = monthBounds(viewYear, viewMonth);

  const { presencas: presencasHoje, isLoading: loadingHoje } = usePresencasDiarias({
    data: hojeStr,
    refetchIntervalMs: fallbackPollMs,
  });
  const { presencas: presencasMes, isLoading: loadingMes } = usePresencasDiarias({
    dataInicio: mesInicio,
    dataFim: mesFim,
  });
  const { presencas: presencasDiaSel, isLoading: loadingDiaSel } = usePresencasDiarias({
    data: selectedIso,
  });
  const { eventos: eventosHoje } = useEventosAcesso({
    data: hojeStr,
    refetchIntervalMs: fallbackPollMs,
  });

  const anoLetivo = hoje.getFullYear();
  const consolidacao = useConsolidacao(escolaId || undefined, anoLetivo);

  const dashboardApi = useQuery({
    queryKey: ['dashboard-escola', escolaId, hojeStr],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.escolas.dashboard(escolaId!), {
        params: { data: hojeStr },
      });
      return data as DashboardConsolidado;
    },
    enabled: !!escolaId,
    staleTime: realtimeConnected ? 15_000 : 5_000,
    refetchInterval: fallbackPollMs,
  });

  // O wizard de setup é renderizado via rota /admin/setup (Dashboard redireciona).
  // A flag `prontoParaOperar` do useSetupStatus detecta se a escola tem
  // turnos, matérias, turmas, etc. — se não tem, mostrarWizard = true.
  // IMPORTANTE: NÃO filtrar por cargo — todas as etapas devem ser consideradas.
  // NOTA: removido !user?.acessoDireto da condição — essa flag é de segurança
  // de login (BYPASS_DEVICE_APPROVAL), não tem relação com setup de escola.
  const setupResult = useSetupStatus();
  const { prontoParaOperar, isLoading: setupLoading } = setupResult;
  const mostrarWizard = (user?.perfil === 'ESCOLA' && escolaId) ? !prontoParaOperar : false;

  const alunosAtivos = alunos.filter((a) => a.ativo).length;
  const turmasAtivas = turmas.filter((t) => t.ativo).length;
  const profsAtivos = professores.filter((p) => p.ativo).length;

  const cardsApi = dashboardApi.data?.cards ?? {};
  const escopoDiaApi = dashboardApi.data?.escopos?.dia ?? cardsApi;
  const escopoTurnoApi = dashboardApi.data?.escopos?.turno;
  const aderenciaDia =
    escopoDiaApi.aderenciaChamadas ?? dashboardApi.data?.analitico?.aderenciaChamadas;

  // ── Escopo DIA (hero / torre principal) ─────────────────────────────
  const presentesHoje =
    typeof escopoDiaApi.totalPresentes === 'number'
      ? escopoDiaApi.totalPresentes
      : presencasHoje.filter(
          (p) => p.status === 'PRESENTE' || p.status === 'ATRASADO',
        ).length;
  const ausentesHoje =
    typeof escopoDiaApi.totalAusentes === 'number'
      ? escopoDiaApi.totalAusentes
      : presencasHoje.filter((p) => p.status === 'AUSENTE').length;

  // Denominador DIA = censo da escola (nunca misturar com turno).
  const censoAlunos =
    typeof dashboardApi.data?.censo?.alunos === 'number'
      ? dashboardApi.data.censo.alunos
      : alunosAtivos;
  const baseAlunosDia =
    censoAlunos > 0
      ? censoAlunos
      : typeof escopoDiaApi.totalPrevistos === 'number'
        ? escopoDiaApi.totalPrevistos
        : alunosAtivos;

  const temRegistroPedagogico =
    (typeof aderenciaDia?.comChamada === 'number'
      ? aderenciaDia.comChamada
      : [...new Set(presencasHoje.map((p) => p.turmaId))].length) > 0 ||
    presentesHoje + ausentesHoje > 0 ||
    presencasHoje.length > 0;

  const portariaCount = eventosHoje.length;
  /** Portaria já registrou e ainda não há chamada/presença pedagógica. */
  const semChamadaComPortaria = !temRegistroPedagogico && portariaCount > 0;

  /** % no dia = presentes / censo da escola. */
  const pctPresenca =
    temRegistroPedagogico && baseAlunosDia > 0
      ? Math.round((presentesHoje / baseAlunosDia) * 100)
      : null;

  /** % só entre alunos já lançados na chamada (secundário). */
  const pctPresencaNasChamadas =
    presentesHoje + ausentesHoje > 0
      ? Math.round((presentesHoje / (presentesHoje + ausentesHoje)) * 100)
      : null;

  const chamadasRealizadas =
    typeof aderenciaDia?.comChamada === 'number'
      ? aderenciaDia.comChamada
      : [...new Set(presencasHoje.map((p) => p.turmaId))].length;
  const turmasChamadaTotal =
    typeof aderenciaDia?.totalTurmas === 'number' && aderenciaDia.totalTurmas > 0
      ? aderenciaDia.totalTurmas
      : turmasAtivas;

  const inconsistencias =
    typeof escopoDiaApi.totalInconsistencias === 'number'
      ? escopoDiaApi.totalInconsistencias
      : typeof cardsApi.totalInconsistencias === 'number'
        ? cardsApi.totalInconsistencias
        : presencasHoje.filter(
            (p) => p.inconsistencia || (p.entrouNaEscola && p.status === 'AUSENTE'),
          ).length;

  // ── Escopo TURNO ATIVO (faixa operacional) ───────────────────────────
  const turnoOperacional =
    (typeof escopoTurnoApi?.nome === 'string' && escopoTurnoApi.nome) ||
    (typeof dashboardApi.data?.turnoOperacional === 'string'
      ? dashboardApi.data.turnoOperacional
      : null);

  const baseAlunosTurno =
    typeof escopoTurnoApi?.totalPrevistos === 'number'
      ? escopoTurnoApi.totalPrevistos
      : 0;
  const presentesTurno =
    typeof escopoTurnoApi?.totalPresentes === 'number'
      ? escopoTurnoApi.totalPresentes
      : 0;
  const ausentesTurno =
    typeof escopoTurnoApi?.totalAusentes === 'number'
      ? escopoTurnoApi.totalAusentes
      : 0;
  const chamadasTurnoRealizadas =
    typeof escopoTurnoApi?.aderenciaChamadas?.comChamada === 'number'
      ? escopoTurnoApi.aderenciaChamadas.comChamada
      : 0;
  const turmasTurnoTotal =
    typeof escopoTurnoApi?.aderenciaChamadas?.totalTurmas === 'number'
      ? escopoTurnoApi.aderenciaChamadas.totalTurmas
      : 0;
  const inconsistenciasTurno =
    typeof escopoTurnoApi?.totalInconsistencias === 'number'
      ? escopoTurnoApi.totalInconsistencias
      : 0;
  const portariaTurno =
    typeof escopoTurnoApi?.totalEntraram === 'number'
      ? escopoTurnoApi.totalEntraram
      : null;
  const temRegistroTurno =
    chamadasTurnoRealizadas > 0 || presentesTurno + ausentesTurno > 0;
  const pctPresencaTurno =
    temRegistroTurno && baseAlunosTurno > 0
      ? Math.round((presentesTurno / baseAlunosTurno) * 100)
      : null;
  const semRegistroTurno = Math.max(
    0,
    baseAlunosTurno - presentesTurno - ausentesTurno,
  );

  const turnoChartData = useMemo(
    () =>
      turnos
        .filter((t) => t.ativo)
        .map((turno) => ({
          turno: turno.nome.length > 12 ? `${turno.nome.slice(0, 10)}…` : turno.nome,
          matriculados: alunos.filter(
            (a) =>
              a.ativo &&
              turmas.find((tr) => tr.id === a.turmaId)?.turnoOperacionalId === turno.id,
          ).length,
        }))
        .filter((row) => row.matriculados > 0),
    [turnos, alunos, turmas],
  );

  const gradeChamadasPorTurma = useMemo(() => {
    const fromApi =
      dashboardApi.data?.operacao?.gradeChamadasPorTurma ??
      dashboardApi.data?.analitico?.gradeChamadasPorTurma;
    if (fromApi?.length) return fromApi;
    const chamadas =
      dashboardApi.data?.operacao?.chamadasDoDia ??
      dashboardApi.data?.analitico?.chamadasDoDia;
    if (chamadas?.length) return gradeFromChamadasDoDia(chamadas);
    return [] as GradeChamadasTurma[];
  }, [dashboardApi.data]);

  const gradeByTurmaId = useMemo(() => {
    const map = new Map<string, GradeChamadasTurma>();
    for (const g of gradeChamadasPorTurma) map.set(g.turmaId, g);
    return map;
  }, [gradeChamadasPorTurma]);

  const turmasStatus = useMemo(() => {
    const source = selectedIso === hojeStr ? presencasHoje : presencasDiaSel;
    const turnoNomeById = new Map(turnos.map((t) => [t.id, t.nome]));

    return turmas
      .filter((t) => t.ativo)
      .map((turma) => {
        const alunosTurma = alunos.filter((a) => a.ativo && a.turmaId === turma.id).length;
        const presencasTurma = source.filter((p) => p.turmaId === turma.id);
        const temRegistro = presencasTurma.length > 0;
        const pres = presencasTurma.filter(
          (p) => p.status === 'PRESENTE' || p.status === 'ATRASADO',
        ).length;
        const aus = presencasTurma.filter((p) => p.status === 'AUSENTE').length;
        // % = presentes / matriculados da turma (nunca / só quem já tem linha de presença).
        const pct =
          temRegistro && alunosTurma > 0
            ? Math.round((pres / alunosTurma) * 100)
            : null;
        const semReg = Math.max(0, alunosTurma - pres - aus);
        // Nome oficial do turno operacional cadastrado (nunca inventar rótulo).
        const turnoNome =
          (turma.turnoOperacionalId && turnoNomeById.get(turma.turnoOperacionalId)) ||
          turma.turnoOperacional?.nome ||
          null;
        const grade = gradeByTurmaId.get(turma.id);
        const aulas = grade?.aulas ?? [];
        const chamadasEsperadas = grade?.chamadasEsperadas ?? aulas.length;
        const chamadasFeitas =
          grade?.chamadasFeitas ??
          aulas.filter((a) => a.chamadaId && a.status !== 'PENDENTE').length;
        // Preferir última chamada da grade (fonte operacional) sobre presença diária esparsa.
        const resumo = grade?.resumoChamada;
        const baseCh = resumo?.totalAlunos && resumo.totalAlunos > 0
          ? resumo.totalAlunos
          : alunosTurma;
        const presOp = resumo ? resumo.totalPresentes : pres;
        const ausOp = resumo ? resumo.totalAusentes : aus;
        const temChamadaOp = chamadasFeitas > 0 || temRegistro;
        const pctOp =
          temChamadaOp && baseCh > 0
            ? Math.round((presOp / baseCh) * 100)
            : pct;
        const critico = temChamadaOp && pctOp !== null && pctOp < 70;
        return {
          ...turma,
          alunos: alunosTurma,
          pres: presOp,
          aus: ausOp,
          semReg: resumo
            ? Math.max(0, baseCh - presOp - ausOp)
            : semReg,
          pct: pctOp,
          temRegistro: temChamadaOp,
          critico,
          resumoChamada: resumo ?? null,
          ausentesUnicosDia: grade?.ausentesUnicosDia ?? [],
          turnoNome,
          turnoId: turma.turnoOperacionalId ?? turma.turnoOperacional?.id ?? null,
          aulas,
          chamadasEsperadas,
          chamadasFeitas,
        };
      })
      .sort((a, b) => {
        if (a.temRegistro !== b.temRegistro) return a.temRegistro ? -1 : 1;
        if ((b.pct ?? -1) !== (a.pct ?? -1)) return (b.pct ?? -1) - (a.pct ?? -1);
        return b.alunos - a.alunos || a.nome.localeCompare(b.nome, 'pt-BR');
      });
  }, [
    turmas,
    turnos,
    alunos,
    presencasHoje,
    presencasDiaSel,
    selectedIso,
    hojeStr,
    gradeByTurmaId,
  ]);

  /** Grupos na ordem dos turnos; `isTurnoAtivo` marca o turno vigente do relógio. */
  const turmasPorTurno = useMemo(() => {
    type Grupo = {
      turnoId: string | null;
      turnoNome: string;
      turmas: typeof turmasStatus;
      alunos: number;
      comChamada: number;
      isTurnoAtivo: boolean;
    };

    const sortTurmas = (list: typeof turmasStatus) =>
      [...list].sort(
        (a, b) =>
          b.alunos - a.alunos ||
          a.nome.localeCompare(b.nome, 'pt-BR'),
      );

    const turnosOrdenados = [...turnos]
      .filter((t) => t.ativo !== false)
      .sort((a, b) =>
        String(a.horaAberturaPortoes || '').localeCompare(
          String(b.horaAberturaPortoes || ''),
        ),
      );

    const used = new Set<string>();
    const grupos: Grupo[] = [];
    const nomeAtivo = (turnoOperacional || '').trim().toLowerCase();

    for (const turno of turnosOrdenados) {
      const items = turmasStatus.filter((t) => t.turnoId === turno.id);
      if (!items.length) continue;
      items.forEach((t) => used.add(t.id));
      grupos.push({
        turnoId: turno.id,
        turnoNome: turno.nome,
        turmas: sortTurmas(items),
        alunos: items.reduce((s, t) => s + t.alunos, 0),
        comChamada: items.filter((t) => t.temRegistro).length,
        isTurnoAtivo: nomeAtivo.length > 0 && turno.nome.trim().toLowerCase() === nomeAtivo,
      });
    }

    const orfas = turmasStatus.filter((t) => !used.has(t.id));
    if (orfas.length) {
      grupos.push({
        turnoId: null,
        turnoNome: 'Sem turno cadastrado',
        turmas: sortTurmas(orfas),
        alunos: orfas.reduce((s, t) => s + t.alunos, 0),
        comChamada: orfas.filter((t) => t.temRegistro).length,
        isTurnoAtivo: false,
      });
    }

    // Turno ativo primeiro na lista
    return [...grupos].sort((a, b) => Number(b.isTurnoAtivo) - Number(a.isTurnoAtivo));
  }, [turmasStatus, turnos, turnoOperacional]);

  /** Censo estrutural por turno (ordem operacional dos turnos, não o ativo primeiro). */
  const censoPorTurno = useMemo(() => {
    const turnosOrdenados = [...turnos]
      .filter((t) => t.ativo !== false)
      .sort((a, b) =>
        String(a.horaAberturaPortoes || '').localeCompare(
          String(b.horaAberturaPortoes || ''),
        ),
      );
    const nomeAtivo = (turnoOperacional || '').trim().toLowerCase();
    const rows: Array<{
      turnoId: string | null;
      turnoNome: string;
      alunos: number;
      turmas: number;
      professores: number;
      isTurnoAtivo: boolean;
    }> = [];
    const countedTurmaIds = new Set<string>();

    const pushGrupo = (
      turnoId: string | null,
      turnoNome: string,
      items: typeof turmasStatus,
      isTurnoAtivo: boolean,
    ) => {
      if (!items.length) return;
      items.forEach((t) => countedTurmaIds.add(t.id));
      const profIds = new Set<string>();
      for (const t of items) {
        for (const a of t.aulas ?? []) {
          if (a.professorId) profIds.add(a.professorId);
        }
      }
      rows.push({
        turnoId,
        turnoNome,
        alunos: items.reduce((s, t) => s + t.alunos, 0),
        turmas: items.length,
        professores: profIds.size,
        isTurnoAtivo,
      });
    };

    for (const turno of turnosOrdenados) {
      pushGrupo(
        turno.id,
        turno.nome,
        turmasStatus.filter((t) => t.turnoId === turno.id),
        nomeAtivo.length > 0 && turno.nome.trim().toLowerCase() === nomeAtivo,
      );
    }

    pushGrupo(
      null,
      'Sem turno',
      turmasStatus.filter((t) => !countedTurmaIds.has(t.id)),
      false,
    );
    return rows;
  }, [turmasStatus, turnos, turnoOperacional]);

  const countByDay = useMemo(() => {
    const map: Record<string, number> = {};
    presencasMes.forEach((p) => {
      const iso = (p.data || '').split('T')[0];
      if (!iso) return;
      map[iso] = (map[iso] ?? 0) + 1;
    });
    // Portaria sozinha também marca o dia no calendário (sem chamar presença 0%).
    if (portariaCount > 0) {
      map[hojeStr] = Math.max(map[hojeStr] ?? 0, portariaCount);
    }
    return map;
  }, [presencasMes, portariaCount, hojeStr]);

  const semRegistroHoje = Math.max(0, baseAlunosDia - presentesHoje - ausentesHoje);

  const operacaoHojeInsight = semChamadaComPortaria
    ? `Sem chamada no dia · ${portariaCount} na portaria`
    : temRegistroPedagogico
      ? semRegistroHoje > 0
        ? `Presença ${pctPresenca ?? 0}% no dia · ${presentesHoje}/${baseAlunosDia} · ${chamadasRealizadas}/${turmasChamadaTotal} chamadas`
        : `Presença ${pctPresenca ?? 0}% no dia · ${chamadasRealizadas}/${turmasChamadaTotal} chamadas`
      : portariaCount === 0
        ? 'Aguardando portaria e chamadas no dia'
        : `Sem chamada no dia · ${portariaCount} na portaria`;

  const entradasPortariaTurno = portariaTurno ?? 0;
  const gapChamadaPortaria =
    temRegistroTurno && portariaTurno != null
      ? Math.max(0, presentesTurno - entradasPortariaTurno)
      : 0;

  /** Texto curto para mobile/lateral — linguagem leiga. */
  const turnoAtivoInsight =
    turnoOperacional && baseAlunosTurno > 0
      ? temRegistroTurno
        ? `${turnoOperacional}: ${presentesTurno} presentes na chamada de ${baseAlunosTurno} alunos` +
          (portariaTurno != null
            ? ` · ${portariaTurno} lidos no RFID da portaria`
            : '') +
          (gapChamadaPortaria > 0
            ? ` (${gapChamadaPortaria} na chamada sem leitura RFID)`
            : '')
        : `${turnoOperacional}: ${baseAlunosTurno} alunos no turno · aguardando chamada`
      : null;

  /** Risco LDB (&lt;75% freq.) por aluno no mês — agrega presenças do período */
  const { riscoChartData, totalEmRisco } = useMemo(() => {
    const byAluno = new Map<
      string,
      { turmaId: string; presentes: number; total: number }
    >();

    presencasMes.forEach((p) => {
      if (!p.alunoId) return;
      const cur = byAluno.get(p.alunoId) ?? {
        turmaId: p.turmaId,
        presentes: 0,
        total: 0,
      };
      cur.total += 1;
      if (p.status === 'PRESENTE' || p.status === 'ATRASADO') cur.presentes += 1;
      byAluno.set(p.alunoId, cur);
    });

    const riscoPorTurma = new Map<string, number>();
    let emRisco = 0;

    byAluno.forEach((agg) => {
      if (agg.total < 1) return;
      const pct = Math.round((agg.presentes / agg.total) * 100);
      if (pct < 75) {
        emRisco += 1;
        riscoPorTurma.set(agg.turmaId, (riscoPorTurma.get(agg.turmaId) ?? 0) + 1);
      }
    });

    const chart = turmas
      .filter((t) => t.ativo)
      .map((t) => ({
        turma: shortTurmaLabel(t.nome),
        emRisco: riscoPorTurma.get(t.id) ?? 0,
      }))
      .sort((a, b) => b.emRisco - a.emRisco)
      .slice(0, 6);

    const fromApi = consolidacao.data?.resumo?.alunosEmRisco;
    const total =
      typeof fromApi === 'number' && fromApi >= 0 ? fromApi : emRisco;

    return {
      riscoChartData: chart,
      totalEmRisco: total,
    };
  }, [presencasMes, turmas, alunos, consolidacao.data?.resumo?.alunosEmRisco]);

  const dadosExemploQuery = useQuery({
    queryKey: ['escola-dados-exemplo-status', escolaId],
    queryFn: async () => {
      if (!escolaId) return { temDadosExemplo: false };
      const { data } = await api.get(ROUTES.escolas.dadosExemploStatus(escolaId));
      return data;
    },
    enabled: !!escolaId,
  });

  const [limpandoDadosExemplo, setLimpandoDadosExemplo] = useState(false);

  const handleLimparDadosExemplo = async () => {
    if (!escolaId) return;
    setLimpandoDadosExemplo(true);
    try {
      const { data } = await api.post(ROUTES.escolas.limparDadosExemplo(escolaId));
      toast.success(
        `Dados de exemplo removidos com sucesso! (${data.removidos.alunos} alunos, ${data.removidos.professores} professores, ${data.removidos.responsaveis} responsáveis).`
      );
      dadosExemploQuery.refetch();
      dashboardApi.refetch?.();
      consolidacao.refetch?.();
      window.location.reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao limpar dados de exemplo');
    } finally {
      setLimpandoDadosExemplo(false);
    }
  };

  return {
    escolaId,
    nomeEscola,
    user,
    hojeStr,
    dataFormatada,
    dataCurta,
    diaReferenciaLabel,
    isSimulation,
    mostrarWizard,
    setupLoading,
    prontoParaOperar,
    selectedIso,
    setSelectedIso,
    viewYear,
    setViewYear,
    viewMonth,
    setViewMonth,
    alunosAtivos,
    turmasAtivas,
    profsAtivos,
    censoPorTurno,
    presentesHoje,
    ausentesHoje,
    semRegistroHoje,
    pctPresenca,
    pctPresencaNasChamadas,
    baseAlunosDia,
    semChamadaComPortaria,
    operacaoHojeInsight,
    turnoAtivoInsight,
    portariaCount,
    chamadasRealizadas,
    turmasChamadaTotal,
    turnoOperacional,
    presentesTurno,
    ausentesTurno,
    baseAlunosTurno,
    semRegistroTurno,
    pctPresencaTurno,
    chamadasTurnoRealizadas,
    turmasTurnoTotal,
    inconsistenciasTurno,
    portariaTurno,
    temRegistroTurno,
    inconsistencias,
    eventosHoje,
    realtimeConnected,
    fallbackPollMs,
    turnoChartData,
    riscoChartData,
    totalEmRisco,
    turmasStatus,
    turmasPorTurno,
    countByDay,
    presencasDiaSel,
    loadingHoje,
    loadingMes,
    loadingDiaSel,
    loadingConsolidacao: consolidacao.isLoading,
    dashboardLoading: dashboardApi.isLoading,
    alertasAtivos: cardsApi.alertasAtivos ?? 0,
    totalPrevistos: cardsApi.totalPrevistos ?? alunosAtivos,
    temDadosExemplo: dadosExemploQuery.data?.temDadosExemplo ?? false,
    dadosExemploInfo: dadosExemploQuery.data,
    limpandoDadosExemplo,
    handleLimparDadosExemplo,
  };
}

export type AdminDashboardPageContext = ReturnType<typeof useAdminDashboardPage>;
